import { useState, useCallback } from 'react';
import { getChatCompletion, getStreamingChatCompletion } from '../services/aiIntegrations/chatCompletion';

/**
 * Unified chat hook - works with any provider
 * 
 * @param {string} provider - Provider identifier (e.g., 'ANTHROPIC', 'OPENAI')
 * @param {string} model - Model name
 * @param {boolean} streaming - Enable streaming mode
 * 
 * @returns {object} { response, fullResponse, isLoading, error, sendMessage }
 * - response: Extracted content string (for simple use cases)
 * - fullResponse: Full SDK response - array of chunks (streaming) or response object (non-streaming)
 */
export function useChat(provider, model, streaming = true) {
  const [response, setResponse] = useState('');
  const [fullResponse, setFullResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const sendMessage = useCallback(
    async (messages, options = {}) => {
      setResponse('');
      setFullResponse(streaming ? [] : null);
      setIsLoading(true);
      setError(null);

      try {
        if (streaming) {
          await getStreamingChatCompletion(
            provider,
            model,
            messages,
            (chunk) => {
              // Store full chunk for advanced use cases
              setFullResponse(prev => [...prev, chunk]);
              // Extract content for simple use cases
              let content = chunk?.choices?.[0]?.delta?.content;
              if (content) {
                setResponse(prev => prev + content);
              }
            },
            () => setIsLoading(false),
            (err) => {
              setError(err);
              setIsLoading(false);
            },
            options
          );
        } else {
          const result = await getChatCompletion(provider, model, messages, options);
          // Store full response for advanced use cases
          setFullResponse(result);

          // Log full result for environment debugging
          console.log('[useChat] Full Lambda result type:', typeof result);
          console.log('[useChat] Full Lambda result:', JSON.stringify(result)?.slice(0, 500));

          // Robustly extract content — handles both parsed object and raw string
          let content = '';
          if (typeof result === 'string') {
            // Lambda returned a raw string — this is the content itself
            content = result;
          } else if (result?.choices?.[0]?.message?.content != null) {
            content = result?.choices?.[0]?.message?.content;
          } else if (result?.content) {
            // Some SDK versions return { content: [...] } for Anthropic
            if (Array.isArray(result?.content)) {
              content = result?.content?.filter(b => b?.type === 'text')?.map(b => b?.text || '')?.join('');
            } else if (typeof result?.content === 'string') {
              content = result?.content;
            }
          }

          console.log('[useChat] Extracted content (first 200 chars):', content?.slice(0, 200));
          setResponse(content);
          setIsLoading(false);
        }
      } catch (err) {
        setError(err);
        setIsLoading(false);
      }
    },
    [provider, model, streaming]
  );

  return { response, fullResponse, isLoading, error, sendMessage };
}